import { accountingFormatNumber, formatNumber } from 'utils';
import { EnterpriseLBAExperienceInterface } from 'components/Applications/EnterpriseLBAForm/type';
import { CurrencyFormatDisplay } from 'components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import { Accordion, AccordionItem } from 'components/UI/Accordion';

interface IExperienceDetailsProps {
  experience: EnterpriseLBAExperienceInterface;
}
export default function ExperienceDetails({
  experience,
}: IExperienceDetailsProps) {
  return (
    <>
      <section aria-labelledby='applicant-information-title'>
        <div className='bg-white shadow sm:rounded-lg'>
          <div className='py-5'>
            <div className='px-4 py-2 sm:px-6 border-l-4 border-primaryColor-normal'>
              <h2
                id='applicant-information-title'
                className='m-0 text-lg leading-6 font-medium text-primaryColor-normal'
              >
                Experience
              </h2>
            </div>
          </div>
          <div className='border-t border-gray-200 px-4 py-5 sm:px-6'>
            <h3 className='mb-5'>Company Completed</h3>
            {experience.companyCompleted.length > 0 && (
              <Accordion multi>
                <div className='space-y-2'>
                  {experience.companyCompleted.map((project: any) => (
                    <AccordionItem
                      key={project.id}
                      id={project.id}
                      title={
                        <div className='flex items-center'>
                          <div>
                            <p className='font-bold'>{project.projectName}</p>
                            <span className='text-neutralColor-normal'>
                              {project.streetAddress}, {project.city},{' '}
                              {project.state}, {project.zipCode}
                            </span>
                          </div>
                        </div>
                      }
                    >
                      <div className='border-t py-5 grid grid-cols-12 gap-5'>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Date Completed
                          </span>
                          <span className='col-span-3'>
                            {project.dateCompleted}
                          </span>
                        </div>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Gross Building Area
                          </span>
                          <span className='col-span-3'>
                            {formatNumber(project.grossBuildingArea ?? 0)}
                          </span>
                        </div>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Number of Housing Units
                          </span>
                          <span className='col-span-3'>
                            {formatNumber(project.housingUnits ?? 0)}
                          </span>
                        </div>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Total Development Cost
                          </span>
                          <span className='col-span-3'>
                            <CurrencyFormatDisplay
                              value={project.grossBuildingArea ?? 0}
                            />
                          </span>
                        </div>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Project Type
                          </span>
                          <span className='col-span-3'>
                            {project?.projectTypes?.map((p: any) => `${p}, `)}
                          </span>
                        </div>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Construction Type
                          </span>
                          <span className='col-span-3'>
                            {project?.constructionTypes?.map(
                              (c: any) => `${c}, `
                            )}
                          </span>
                        </div>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Company's Role
                          </span>
                          <span className='col-span-3'>
                            {project?.companyRole?.map((cr: any) => `${cr}, `)}
                            {project?.partialOwner && (
                              <>Partial Owner({project?.partialOwner}%)</>
                            )}
                          </span>
                        </div>
                      </div>
                    </AccordionItem>
                  ))}
                </div>
              </Accordion>
            )}
          </div>
          <div className='px-4 py-5 sm:px-6'>
            <h3 className='mb-5'>Principal Project Completed</h3>
            {experience?.principalCompleted.length > 0 && (
              <Accordion multi>
                <div className='space-y-2'>
                  {experience.principalCompleted.map((project: any) => (
                    <AccordionItem
                      key={project.id}
                      id={project.id}
                      title={
                        <div
                          key={project.id}
                          className='flex justify-between items-center'
                        >
                          <div>
                            <p className='font-bold'>{project.projectName}</p>
                            <span className='text-neutralColor-normal'>
                              {project.streetAddress}, {project.city},{' '}
                              {project.state}, {project.zipCode}
                            </span>
                          </div>
                        </div>
                      }
                    >
                      <div className='border-t py-5 grid grid-cols-12 gap-5'>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Company name
                          </span>
                          <span className='col-span-3'>
                            {project.companyName}
                          </span>
                        </div>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Date Completed
                          </span>
                          <span className='col-span-3'>
                            {project.dateCompleted}
                          </span>
                        </div>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Gross Building Area
                          </span>
                          <span className='col-span-3'>
                            {formatNumber(project.grossBuildingArea ?? 0)}
                          </span>
                        </div>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Number of Housing Units
                          </span>
                          <span className='col-span-3'>
                            {formatNumber(project.housingUnits ?? 0)}
                          </span>
                        </div>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Total Development Cost
                          </span>
                          <span className='col-span-3'>
                            <CurrencyFormatDisplay
                              value={project.grossBuildingArea ?? 0}
                            />
                          </span>
                        </div>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Project Type
                          </span>
                          <span className='col-span-3'>
                            {project?.projectTypes?.map((p: any) => `${p}, `)}
                          </span>
                        </div>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Construction Type
                          </span>
                          <span className='col-span-3'>
                            {project?.constructionTypes?.map(
                              (c: any) => `${c}, `
                            )}
                          </span>
                        </div>
                        <div className='col-span-6 grid grid-cols-6 gap-4'>
                          <span className='col-span-3 text-neutralColor-normal'>
                            Company's Role
                          </span>
                          <span className='col-span-3'>
                            {project?.companyRole?.map((cr: any) => `${cr}, `)}
                            {project?.partialOwner && (
                              <>Partial Owner({project?.partialOwner}%)</>
                            )}
                          </span>
                        </div>
                      </div>
                    </AccordionItem>
                  ))}
                </div>
              </Accordion>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
